import { supabase } from '@/utils/supabase';

export type FraudAlertReviewStatus =
  | 'open'
  | 'reviewed_false_alarm'
  | 'reviewed_amended_return_accurate';

export type FraudAlertDisposition = 'false_alarm' | 'amended_return_accurate';

export interface FraudAlert {
  id: string;
  organizationId: string | null;
  encompassLoanId: string;
  detectedAt: string;
  reviewStatus: FraudAlertReviewStatus;
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  detectorVersion: string;
  evidencePages: number[];
  totalWeight?: number;
  documentName?: string | null;
  documentFolder?: string | null;
  attachmentState: 'pending' | 'included' | 'omitted' | 'unknown';
  attachmentReason: string | null;
  indicators: string[];
  confidence: string;
  notified: boolean;
  notifiedAt: string | null;
  sourceType: string;
}

export interface FraudAlertLoanSummary {
  id: string;
  encompassLoanId: string;
  loanNumber: string;
  borrowerName: string;
  loanOfficerName: string;
  processorName: string;
}

export interface FraudAlertReportRow {
  alert: FraudAlert;
  loan: FraudAlertLoanSummary | null;
}

interface DetectionRow {
  id: string;
  organization_id: string | null;
  encompass_loan_id: string;
  detected_at: string;
  review_status: FraudAlertReviewStatus;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  detector_version: string;
  evidence_pages: number[] | null;
  total_weight: number;
  document_id: string | null;
  attachment_id: string | null;
  attachment_state: FraudAlert['attachmentState'];
  attachment_reason: string | null;
  indicators: string[] | null;
  confidence: string;
  notified: boolean;
  notified_at: string | null;
  source_type: string;
}

interface AttachmentRow {
  id: string;
  title: string | null;
  file_name: string | null;
  assigned_folder: string | null;
}

interface LoanDocumentRow {
  id: string;
  title: string | null;
  original_filename: string | null;
  smart_filename: string | null;
}

interface DetectionDocumentContext {
  documentName: string | null;
  documentFolder: string | null;
}

interface LoanRow {
  id: string;
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  loan_processor_first_name: string | null;
  loan_processor_last_name: string | null;
}

const DETECTION_COLUMNS = [
  'id',
  'organization_id',
  'encompass_loan_id',
  'detected_at',
  'review_status',
  'review_note',
  'reviewed_by',
  'reviewed_at',
  'detector_version',
  'evidence_pages',
  'total_weight',
  'document_id',
  'attachment_id',
  'attachment_state',
  'attachment_reason',
  'indicators',
  'confidence',
  'notified',
  'notified_at',
  'source_type'
].join(', ');

const LOAN_ID_CHUNK_SIZE = 100;
const SOURCE_REFERENCE_CHUNK_SIZE = 100;

function displayName(...parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(' ').trim();
}

function mapDetection(
  row: DetectionRow,
  documentContext?: DetectionDocumentContext
): FraudAlert {
  return {
    id: row.id,
    organizationId: row.organization_id,
    encompassLoanId: row.encompass_loan_id,
    detectedAt: row.detected_at,
    reviewStatus: row.review_status,
    reviewNote: row.review_note,
    reviewedBy: row.reviewed_by,
    reviewedAt: row.reviewed_at,
    detectorVersion: row.detector_version,
    evidencePages: Array.isArray(row.evidence_pages) ? row.evidence_pages : [],
    totalWeight: row.total_weight,
    documentName: documentContext?.documentName ?? null,
    documentFolder: documentContext?.documentFolder ?? null,
    attachmentState: row.attachment_state,
    attachmentReason: row.attachment_reason,
    indicators: Array.isArray(row.indicators) ? row.indicators : [],
    confidence: row.confidence,
    notified: row.notified,
    notifiedAt: row.notified_at,
    sourceType: row.source_type
  };
}

async function loadDocumentContexts(
  rows: DetectionRow[]
): Promise<Map<string, DetectionDocumentContext>> {
  const attachmentIds = [...new Set(rows.map(row => row.attachment_id).filter(Boolean))] as string[];
  const documentIds = [...new Set(rows.map(row => row.document_id).filter(Boolean))] as string[];
  const attachments = new Map<string, AttachmentRow>();
  const loanDocuments = new Map<string, LoanDocumentRow>();

  for (let index = 0; index < attachmentIds.length; index += SOURCE_REFERENCE_CHUNK_SIZE) {
    const idChunk = attachmentIds.slice(index, index + SOURCE_REFERENCE_CHUNK_SIZE);
    const { data, error } = await supabase
      .from('encompass_attachments')
      .select('id, title, file_name, assigned_folder')
      .in('id', idChunk);

    if (error) {
      throw new Error(`Unable to load fraud alert source documents: ${error.message}`);
    }
    for (const attachment of (data ?? []) as AttachmentRow[]) {
      attachments.set(attachment.id, attachment);
    }
  }

  for (let index = 0; index < documentIds.length; index += SOURCE_REFERENCE_CHUNK_SIZE) {
    const idChunk = documentIds.slice(index, index + SOURCE_REFERENCE_CHUNK_SIZE);
    const { data, error } = await supabase
      .from('loan_documents')
      .select('id, title, original_filename, smart_filename')
      .in('id', idChunk);

    if (error) {
      throw new Error(`Unable to load fraud alert source documents: ${error.message}`);
    }
    for (const document of (data ?? []) as LoanDocumentRow[]) {
      loanDocuments.set(document.id, document);
    }
  }

  const contexts = new Map<string, DetectionDocumentContext>();
  for (const row of rows) {
    const attachment = row.attachment_id ? attachments.get(row.attachment_id) : undefined;
    const loanDocument = row.document_id ? loanDocuments.get(row.document_id) : undefined;
    contexts.set(row.id, {
      documentName: attachment?.title
        || attachment?.file_name
        || loanDocument?.smart_filename
        || loanDocument?.title
        || loanDocument?.original_filename
        || null,
      documentFolder: attachment?.assigned_folder || null
    });
  }
  return contexts;
}

async function mapDetections(rows: DetectionRow[]): Promise<FraudAlert[]> {
  const documentContexts = await loadDocumentContexts(rows);
  return rows.map(row => mapDetection(row, documentContexts.get(row.id)));
}

export async function getLoanFraudAlerts(encompassLoanId: string): Promise<FraudAlert[]> {
  const { data, error } = await supabase
    .from('amended_return_detections')
    .select(DETECTION_COLUMNS)
    .eq('encompass_loan_id', encompassLoanId)
    .order('detected_at', { ascending: false });

  if (error) {
    throw new Error(`Unable to load fraud alerts: ${error.message}`);
  }

  return mapDetections((data ?? []) as unknown as DetectionRow[]);
}

export async function reviewFraudAlert(
  detectionId: string,
  disposition: FraudAlertDisposition,
  explanation: string | null
): Promise<void> {
  const { error } = await supabase.rpc('review_amended_return_alert', {
    p_detection_id: detectionId,
    p_disposition: disposition,
    p_explanation: explanation
  });

  if (error) {
    throw new Error(error.message || 'Unable to review fraud alert');
  }
}

export async function getFraudAlertReportRows(): Promise<FraudAlertReportRow[]> {
  const { data: detectionData, error: detectionError } = await supabase
    .from('amended_return_detections')
    .select(DETECTION_COLUMNS)
    .order('detected_at', { ascending: false });

  if (detectionError) {
    throw new Error(`Unable to load fraud alerts: ${detectionError.message}`);
  }

  const alerts = await mapDetections(
    (detectionData ?? []) as unknown as DetectionRow[]
  );
  const encompassLoanIds = [...new Set(alerts.map(alert => alert.encompassLoanId).filter(Boolean))];
  if (encompassLoanIds.length === 0) {
    return alerts.map(alert => ({ alert, loan: null }));
  }

  const loanRows: LoanRow[] = [];
  for (let index = 0; index < encompassLoanIds.length; index += LOAN_ID_CHUNK_SIZE) {
    const loanIdChunk = encompassLoanIds.slice(index, index + LOAN_ID_CHUNK_SIZE);
    const { data: loanData, error: loanError } = await supabase
      .from('encompass_loans')
      .select([
        'id',
        'encompass_loan_id',
        'trans_details_loan_number',
        'borrower_first_name',
        'borrower_last_name',
        'loan_officer_first_name',
        'loan_officer_last_name',
        'loan_processor_first_name',
        'loan_processor_last_name'
      ].join(', '))
      .in('encompass_loan_id', loanIdChunk);

    if (loanError) {
      throw new Error(`Unable to load fraud alert loans: ${loanError.message}`);
    }
    loanRows.push(...((loanData ?? []) as unknown as LoanRow[]));
  }

  const loans = new Map<string, FraudAlertLoanSummary>();
  for (const row of loanRows) {
    loans.set(row.encompass_loan_id, {
      id: row.id,
      encompassLoanId: row.encompass_loan_id,
      loanNumber: row.trans_details_loan_number || 'Unknown loan',
      borrowerName: displayName(row.borrower_first_name, row.borrower_last_name) || 'Unknown borrower',
      loanOfficerName: displayName(row.loan_officer_first_name, row.loan_officer_last_name) || 'Unassigned',
      processorName: displayName(row.loan_processor_first_name, row.loan_processor_last_name) || 'Unassigned'
    });
  }

  return alerts.map(alert => ({
    alert,
    loan: loans.get(alert.encompassLoanId) ?? null
  }));
}
